import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { loginApi, getLocationDetails, getStaffLocations } from '../services/api';
import { setOwnerLocationCount } from '../context/OwnerViewContext';
import { SELECTED_BRANCH_DATA, getLogoUrlFromMedia } from '../constants';
import { pickTimeZoneCdFromLocationPayload } from '../utils/locationPayload';
import { isOnlySpecialCharacters } from '../utils/textValidation';
import brisqueLogoRed from '../assets/BrisqueLogo/svg/brisque-logo-red.svg';
import loginSlideAttractGuests from '../assets/LoginCarousel/desktop/login-slide-attract-guests.webp';
import loginSlideBuildLoyalty from '../assets/LoginCarousel/desktop/login-slide-build-loyalty.webp';
import loginSlideGrowWithData from '../assets/LoginCarousel/desktop/login-slide-grow-with-data.webp';
import loginSlideMaximizeOrders from '../assets/LoginCarousel/desktop/login-slide-maximize-orders.webp';
import loginSlideMobileAttractGuests from '../assets/LoginCarousel/mobile/login-slide-mobile-attract-guests.webp';
import loginSlideMobileBuildLoyalty from '../assets/LoginCarousel/mobile/login-slide-mobile-build-loyalty.webp';
import loginSlideMobileGrowWithData from '../assets/LoginCarousel/mobile/login-slide-mobile-grow-with-data.webp';
import loginSlideMobileMaximizeOrders from '../assets/LoginCarousel/mobile/login-slide-mobile-maximize-orders.webp';
import './LoginScreen.css';

const useIsDesktop = () => {
    const query = '(min-width: 900px)';
    const getMatches = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
    const [isDesktop, setIsDesktop] = useState(getMatches);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const media = window.matchMedia(query);
        const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
        setIsDesktop(media.matches);
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);

    return isDesktop;
};

const EyeIcon = ({ open, size = 22, color = '#6E7380', strokeWidth = 1.8 }: { open: boolean; size?: number; color?: string; strokeWidth?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color, userSelect: "none", display: "block" }}>
        {open ? (
            <>
                <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7z" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth={strokeWidth} />
            </>
        ) : (
            <>
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-5 0-9.27-3.11-11-7a10.17 10.17 0 0 1 2.31-3.65M6.53 6.53A9.96 9.96 0 0 1 12 5c5 0 9.27 3.11 11 7a10.07 10.07 0 0 1-1.3 2.4" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10.73 10.73A2 2 0 0 0 12 14a2 2 0 0 0 1.27-.73M2 2l20 20" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
            </>
        )}
    </svg>
);

const desktopSlides = [
    { image: loginSlideAttractGuests, alt: 'Attract guests: turn online traffic into table reservations' },
    { image: loginSlideMaximizeOrders, alt: 'Maximize orders: capture every cent of intent' },
    { image: loginSlideGrowWithData, alt: 'Grow with data: transform kitchen insights into revenue' },
    { image: loginSlideBuildLoyalty, alt: 'Build loyalty: convert first-timers into brand loyalists' },
];

const mobileSlides = [
    { image: loginSlideMobileAttractGuests, alt: 'Attract guests: turn online traffic into table reservations' },
    { image: loginSlideMobileMaximizeOrders, alt: 'Maximize orders: capture every cent of intent' },
    { image: loginSlideMobileGrowWithData, alt: 'Grow with data: transform kitchen insights into revenue' },
    { image: loginSlideMobileBuildLoyalty, alt: 'Build loyalty: convert first-timers into brand loyalists' },
];

const getLoginIdentityValidationError = (businessName: string, userId: string): string => {
    if (isOnlySpecialCharacters(businessName)) return 'Business Name cannot contain only special characters.';
    if (isOnlySpecialCharacters(userId)) return 'User ID cannot contain only special characters.';
    return '';
};

const DesktopLoginScreen = () => {
    const [businessName, setBusinessName] = useState('');
    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeSlide, setActiveSlide] = useState(0);
    const { login } = useAuth();
    const { setSelectedBranch } = useBranch();
    const navigate = useNavigate();

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveSlide((prev) => (prev + 1) % desktopSlides.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const handleLogin = async () => {
        if (!businessName.trim() || !userId.trim() || !password.trim()) {
            setError('Please enter your Business Name, User ID and Password.');
            return;
        }

        const identityValidationError = getLoginIdentityValidationError(businessName, userId);
        if (identityValidationError) {
            setError(identityValidationError);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const data = await loginApi({
                businessName: businessName.trim(),
                userId: userId.trim(),
                password,
            });

            login(data);

            const locationId = data.locationId || data.location_id;
            const token = data.accessToken || data.access_token;

            try {
                const res = await getLocationDetails(locationId, token);
                const payload = (res as any)?.metaDataInfo?.data ?? (res as any)?.data ?? res;
                const branchList = payload.branch || payload.branches || [];
                const currentBranch = Array.isArray(branchList)
                    ? branchList.find((b) => (b.id || b.locationId) === locationId)
                    : null;

                const timeZoneCd =
                    pickTimeZoneCdFromLocationPayload(payload) ?? pickTimeZoneCdFromLocationPayload(currentBranch);

                const workingHours = payload.workingHours || currentBranch?.workingHours || [];
                if (currentBranch) {
                    const logoUrl = getLogoUrlFromMedia(currentBranch.media || payload.media || []) || currentBranch.logoUrl || '';

                    const branch = {
                        ...currentBranch,
                        id: currentBranch.id || locationId,
                        locationName: currentBranch.locationName || currentBranch.name || 'Current Branch',
                        logoUrl,
                        ...(timeZoneCd ? { timeZoneCd } : {}),
                        workingHours,
                    };

                    localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                    setSelectedBranch(branch);
                } else {
                    const branch = {
                        id: locationId,
                        locationName: data.locationName || data.location_name || 'Current Branch',
                        ...(timeZoneCd ? { timeZoneCd } : {}),
                        workingHours,
                    };

                    localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                    setSelectedBranch(branch);
                }
            } catch (_) {
                const branch = {
                    id: locationId,
                    locationName: data.locationName || data.location_name || 'Current Branch',
                };

                localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                setSelectedBranch(branch);
            }

            navigate('/reports/snapshot', { replace: true });
        } catch (err: any) {
            setError(err.message || 'Your User ID or Password is Incorrect');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleLogin();
    };

    return (
        <div className="login-desktop">
            <div className="login-desktop__stage">
                {desktopSlides.map((slide, index) => (
                    <div
                        key={slide.image}
                        aria-hidden={index !== activeSlide}
                        className={`login-desktop__slide${index === activeSlide ? ' login-desktop__slide--active' : ''}`}
                    >
                        <img className="login-desktop__slide-image" src={slide.image} alt={slide.alt} draggable={false} />
                    </div>
                ))}

                <div className="login-desktop__dots">
                    {desktopSlides.map((slide, index) => (
                        <button
                            key={slide.image}
                            type="button"
                            aria-label={`Show slide ${index + 1}`}
                            onClick={() => setActiveSlide(index)}
                            className={`login-desktop__dot${index === activeSlide ? ' login-desktop__dot--active' : ''}`}
                        />
                    ))}
                </div>

                <div className="login-desktop__legal">
                    <span>© 2026 Brisque. All rights reserved.</span>
                </div>
            </div>

            <div className="login-desktop__panel">
                <div className="login-desktop__brand">Brisque</div>

                <div className="login-desktop__intro">
                    <h1 className="login-desktop__title">Sign In</h1>
                    <p className="login-desktop__subtitle">Please enter your details to access your dashboard.</p>
                </div>

                {error && <div className="login-desktop__error">{error}</div>}

                <div className="login-desktop__form">
                    <div className="login-desktop__fields">
                        <div className="login-desktop__field">
                            <label className="login-desktop__label" htmlFor="login-business-name">Business Name</label>
                            <input
                                id="login-business-name"
                                className="login-desktop__input"
                                type="text"
                                value={businessName}
                                onChange={(e) => setBusinessName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter your business name"
                            />
                        </div>

                        <div className="login-desktop__field">
                            <label className="login-desktop__label" htmlFor="login-user-id">User ID</label>
                            <input
                                id="login-user-id"
                                className="login-desktop__input"
                                type="text"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter your user ID"
                            />
                        </div>

                        <div className="login-desktop__field">
                            <label className="login-desktop__label" htmlFor="login-password">Password</label>
                            <div className="login-desktop__input-wrap">
                                <input
                                    id="login-password"
                                    className="login-desktop__input login-desktop__input--with-toggle"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Enter your password"
                                />
                                <button
                                    type="button"
                                    className="login-desktop__toggle"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    onClick={() => setShowPassword((value) => !value)}
                                >
                                    <EyeIcon open={showPassword} size={24} color="currentColor" strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="login-desktop__submit"
                        onClick={handleLogin}
                        disabled={loading}
                    >
                        {loading ? 'Signing In...' : 'Sign In'}
                    </button>
                </div>

                <div className="login-desktop__powered">
                    <span>Powered by</span>
                    <img className="login-desktop__powered-logo" src={brisqueLogoRed} alt="Brisque" draggable={false} />
                    <span className="login-desktop__powered-version">v2.0</span>
                </div>
            </div>
        </div>
    );
};

const MobileLoginScreen = () => {
    const [businessName, setBusinessName] = useState('');
    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showSignIn, setShowSignIn] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const { login } = useAuth();
    const { setSelectedBranch } = useBranch();
    const navigate = useNavigate();

    useEffect(() => {
        if (showSignIn) return undefined;
        const interval = setInterval(() => {
            setActiveSlide((prev) => (prev + 1) % mobileSlides.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [showSignIn]);

    const handleLogin = async () => {
        if (!businessName.trim() || !userId.trim() || !password.trim()) {
            setError('Please enter your Business Name, User ID and Password.');
            return;
        }

        const identityValidationError = getLoginIdentityValidationError(businessName, userId);
        if (identityValidationError) {
            setError(identityValidationError);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const data = await loginApi({ businessName: businessName.trim(), userId: userId.trim(), password });

            const locationId = data.locationId || data.location_id;
            const token = data.accessToken || data.access_token;
            try {
                const res = await getLocationDetails(locationId, token);
                const payload = (res as any)?.metaDataInfo?.data ?? (res as any)?.data ?? res;
                const branchList = payload.branch || payload.branches || [];
                const currentBranch = Array.isArray(branchList)
                    ? branchList.find((b) => (b.id || b.locationId) === locationId)
                    : null;
                const timeZoneCd =
                    pickTimeZoneCdFromLocationPayload(payload) ?? pickTimeZoneCdFromLocationPayload(currentBranch);
                const workingHours = payload.workingHours || currentBranch?.workingHours || [];
                if (currentBranch) {
                    const logoUrl = getLogoUrlFromMedia(currentBranch.media || payload.media || []) || currentBranch.logoUrl || '';
                    const branch = {
                        ...currentBranch,
                        id: currentBranch.id || locationId,
                        locationName: currentBranch.locationName || currentBranch.name || 'Current Branch',
                        logoUrl,
                        ...(timeZoneCd ? { timeZoneCd } : {}),
                        workingHours,
                    };
                    localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                    setSelectedBranch(branch);
                } else {
                    const branch = {
                        id: locationId,
                        locationName: data.locationName || data.location_name || 'Current Branch',
                        ...(timeZoneCd ? { timeZoneCd } : {}),
                        workingHours,
                    };
                    localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                    setSelectedBranch(branch);
                }
            } catch (_) {
                const branch = {
                    id: locationId,
                    locationName: data.locationName || data.location_name || 'Current Branch',
                };
                localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                setSelectedBranch(branch);
            }

            const role = data.role || '';
            const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];
            const isSuper = SUPER_ROLES.includes(role);

            // Owner dashboard only makes sense with >1 outlet. Read the outlet
            // dropdown count (same source as OwnerViewContext) so single-branch
            // owners go straight to the snapshot instead of bouncing through it.
            // null = unknown (fetch failed) -> fall back to owner-view (it self-redirects).
            let locationCount: number | null = null;
            if (isSuper) {
                try {
                    const staffId = data.id || data.staff_id || '';
                    const res = await getStaffLocations(staffId, token);
                    const payload = (res as any)?.metaDataInfo?.data ?? (res as any)?.data ?? res;
                    const list = Array.isArray(payload) ? payload : payload?.branch ?? payload?.branches ?? [];
                    locationCount = Array.isArray(list) ? list.length : 0;
                } catch {
                    locationCount = null;
                }
            }

            // Seed the shared outlet-count cache BEFORE login() flips auth, so the
            // post-login redirect (homePath in App.tsx) lands on the right page with
            // no snapshot<->owner-view bounce. Non-super users reset to 0 (never owner-view).
            setOwnerLocationCount(isSuper ? locationCount : 0);

            login(data);

            const goOwnerView = isSuper && (locationCount === null || locationCount > 1);
            navigate(goOwnerView ? '/owner-view' : '/reports/snapshot', { replace: true });
        } catch (err: any) {
            setError(err.message || 'Your User ID or Password is Incorrect');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleLogin();
    };

    if (!showSignIn) {
        return (
            <div className="login-mobile">
                {mobileSlides.map((slide, index) => (
                    <img
                        key={slide.image}
                        src={slide.image}
                        alt={slide.alt}
                        aria-hidden={index !== activeSlide}
                        draggable={false}
                        className={`login-mobile__slide${index === activeSlide ? ' login-mobile__slide--active' : ''}`}
                    />
                ))}
                <div className="login-mobile__fade" />

                <div className="login-mobile__dots">
                    {mobileSlides.map((slide, index) => (
                        <button
                            key={slide.image}
                            type="button"
                            aria-label={`Show slide ${index + 1}`}
                            onClick={() => setActiveSlide(index)}
                            className={`login-mobile__dot${index === activeSlide ? ' login-mobile__dot--active' : ''}`}
                        />
                    ))}
                </div>

                <button type="button" className="login-mobile__cta" onClick={() => setShowSignIn(true)}>
                    <span>Get Started</span>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                <div className="login-mobile__legal">
                    <span>© 2026 Brisque. All rights reserved.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="login-desktop login-desktop--single">
            <div className="login-desktop__panel">
                <div className="login-desktop__brand">Brisque</div>

                <div className="login-desktop__intro">
                    <h1 className="login-desktop__title">Sign In</h1>
                    <p className="login-desktop__subtitle">Please enter your details to access</p>
                </div>

                {error && <div className="login-desktop__error">{error}</div>}

                <div className="login-desktop__form">
                    <div className="login-desktop__fields">
                        <div className="login-desktop__field">
                            <label className="login-desktop__label" htmlFor="m-login-business-name">Business Name</label>
                            <input
                                id="m-login-business-name"
                                className="login-desktop__input"
                                type="text"
                                value={businessName}
                                onChange={(e) => setBusinessName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter your business name"
                            />
                        </div>

                        <div className="login-desktop__field">
                            <label className="login-desktop__label" htmlFor="m-login-user-id">User ID</label>
                            <input
                                id="m-login-user-id"
                                className="login-desktop__input"
                                type="text"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter your user ID"
                            />
                        </div>

                        <div className="login-desktop__field">
                            <label className="login-desktop__label" htmlFor="m-login-password">Password</label>
                            <div className="login-desktop__input-wrap">
                                <input
                                    id="m-login-password"
                                    className="login-desktop__input login-desktop__input--with-toggle"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Enter your password"
                                />
                                <button
                                    type="button"
                                    className="login-desktop__toggle"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    onClick={() => setShowPassword((value) => !value)}
                                >
                                    <EyeIcon open={showPassword} size={24} color="currentColor" strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="login-desktop__submit"
                        onClick={handleLogin}
                        disabled={loading}
                    >
                        {loading ? 'Signing In...' : 'Sign In'}
                    </button>
                </div>

                <div className="login-desktop__powered">
                    <span>Powered by</span>
                    <img className="login-desktop__powered-logo" src={brisqueLogoRed} alt="Brisque" draggable={false} />
                    <span className="login-desktop__powered-version">v2.0</span>
                </div>
            </div>
        </div>
    );
};

export default function LoginScreen() {
    const isDesktop = useIsDesktop();
    return isDesktop ? <DesktopLoginScreen /> : <MobileLoginScreen />;
}

